/**
 * Module augmentation for the v8 DataTable column `meta` slot.
 *
 * TanStack Table v8 ships an intentionally-empty `ColumnMeta<TData, TValue>` interface that
 * consumers widen via declaration merging. ALL of our managed layout / totals / sort metadata
 * lives on `ColumnDef.meta`, so widening `ColumnMeta` here makes `column.columnDef.meta`
 * strongly typed EVERYWHERE the table is used — the engine (`DataTable.tsx` `readMeta`), the
 * basic tables, and the managed-layout / sort wrappers — without any of them re-casting `meta`.
 *
 * This lives in a DEDICATED file (not `DataTable.tsx`) so the global augmentation is opt-in via
 * a single import side-effect and the engine file stays free of ambient declarations. Importing
 * the `data-table` barrel (or this module directly) pulls the augmentation into scope.
 *
 * The shape mirrors {@link DataTableColumnMeta}: the reused {@link ManagedColumnMeta}
 * (widthWeight / minPx / maxPx / fixedPx / sticky / defaultHidden / lockVisible / disableResize /
 * pickerLabel) plus the totals, alignment and server-sort slots (`renderTotal`, `textAlign`,
 * `sortAccessor`, `nullHandling`) and the basic tables' literal render options.
 */
import type { RowData } from '@tanstack/react-table';
import type { CSSProperties, ReactNode } from 'react';
import type { ManagedColumnMeta } from '../list-table/columnLayout/types';

/** Server-sort null ordering, so the sort-string builder (`sortUtil`) can append
 *  `,nullsFirst` / `,nullsLast` off the column meta. */
export type NullHandling = 'nullsFirst' | 'nullsLast';

/**
 * A column's name in plain text, for the column picker and for the names of its grip and resize handle: its
 * `pickerLabel`, else its header when that is a string and not a render function, else its id, so that a column
 * with neither still has a name.
 */
export const plainColumnLabel = (meta: { pickerLabel?: string }, header: unknown, id: string): string =>
    meta.pickerLabel || (typeof header === 'string' && header ? header : id);

declare module '@tanstack/react-table' {
    // TValue is part of TanStack's own `ColumnMeta<TData, TValue>` signature; the augmentation
    // must repeat it verbatim to merge. Neither type parameter is referenced in the body — our
    // meta is value-shape-independent — so they are intentionally unused here.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TData/TValue must match TanStack's ColumnMeta<TData, TValue> signature for declaration merging; the managed meta does not depend on either
    interface ColumnMeta<TData extends RowData, TValue> extends ManagedColumnMeta {
        /** Renders this column's totals-row cell when the table's `showTotalsRow` is on. The totals
         *  shape is consumer-specific, so this generic-free augmentation types the parameter `never`
         *  ON PURPOSE: `ColumnDef.meta` is the INTERSECTION of this `ColumnMeta` and
         *  `DataTableColumnMeta<TotalsType>` (see `types.ts`), so a column literal's `renderTotal`
         *  parameter is contextually the UNION of the two declarations — and `never | TotalsType`
         *  collapses to `TotalsType`, letting a typed def infer `totals` as its real totals shape.
         *  `unknown` here would widen that union back to `unknown` and break inference. The engine
         *  reads/calls it via `readMeta<TotalsType>` (a `DataTableColumnMeta<TotalsType>` cast). */
        renderTotal?: (totals: never) => ReactNode;
        /** Text alignment for this column's cells, applied by whichever table renders it: the
         *  DataTable engine sets it on the header, body and totals cells; `BasicTable` on its
         *  header cells; `UltraBasicTable` on its body cells (it renders no header). */
        textAlign?: CSSProperties['textAlign'];
        /** Per-column body-cell padding override, replacing the shared 16px cell inset. */
        padding?: CSSProperties['padding'];
        /** Server-sort field override: the RSQL/sort key sent to the backend when this column is
         *  sorted, when it differs from the column id. */
        sortAccessor?: string;
        /** Server-sort null ordering. */
        nullHandling?: NullHandling;
        /** Class applied to the rendered `<TableCell>` of the basic tables (e.g. `'shrink'` to
         *  disable wrapping). Not read by the managed DataTable engine, which sizes cells from
         *  {@link ManagedColumnMeta}. */
        className?: string;
        /** Width preference written to the inline style of the basic tables' cells — `BasicTable`'s
         *  header cells and `UltraBasicTable`'s body cells. Deliberately outside TanStack's numeric
         *  sizing model, since these carry percentage strings (`'30%'`) as well as pixel numbers,
         *  and the managed DataTable engine resolves its own widths from {@link ManagedColumnMeta}. */
        width?: CSSProperties['width'];
        minWidth?: CSSProperties['minWidth'];
    }
}
